import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { programsAPI, contractsAPI, inventoryAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { SearchableSelect } from '@/components/SearchableSelect';
import {
  ArrowLeft, Plus, X, Upload, Download, FileText, Edit2, Trash2,
  Link2, Unlink, CheckCircle2, Clock, AlertTriangle, ListTodo,
  TrendingUp, Package, ShoppingCart, CalendarClock, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';

export default function ProgramDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerPOs, setCustomerPOs] = useState([]);
  const [users, setUsers] = useState([]);

  const [showLinkPO, setShowLinkPO] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [showDelayModal, setShowDelayModal] = useState(null);
  const [delayForm, setDelayForm] = useState({ new_due_date: '', delay_reason: '' });
  const [selectedPO, setSelectedPO] = useState('');
  const [itemForm, setItemForm] = useState({ item_id: '', item_name: '', item_unique_id: '', item_type: 'sale', quantity: 0, unit_price: 0, description: '' });
  const [itemTypeFilter, setItemTypeFilter] = useState('');
  const [taskForm, setTaskForm] = useState({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', depends_on: [] });
  const [masterItems, setMasterItems] = useState([]);

  const loadProgram = useCallback(async () => {
    try {
      const res = await programsAPI.get(id);
      setProgram(res.data);
      // Load customer POs for linking
      if (res.data.customer_id) {
        const posRes = await contractsAPI.listPurchaseOrders({ customer_id: res.data.customer_id });
        setCustomerPOs(posRes.data.purchase_orders || []);
      }
    } catch { toast.error('Program not found'); navigate('/programs/list'); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => {
    loadProgram();
    (async () => {
      try {
        const [usersRes, itemsRes] = await Promise.all([
          (await import('@/lib/api')).default.get('/users'),
          inventoryAPI.listItems({ limit: 500, sort_by: 'item_name', sort_order: 'asc' }),
        ]);
        setUsers(usersRes.data);
        setMasterItems(itemsRes.data.items || []);
      } catch {}
    })();
  }, [loadProgram]);

  // PO linking
  const handleLinkPO = async () => {
    if (!selectedPO) return;
    try {
      const formData = new FormData();
      formData.append('po_id', selectedPO);
      await programsAPI.linkPO(id, formData);
      toast.success('PO linked');
      setShowLinkPO(false);
      setSelectedPO('');
      loadProgram();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleUnlinkPO = async (poId) => {
    if (!window.confirm('Unlink this PO?')) return;
    try { await programsAPI.unlinkPO(id, poId); toast.success('PO unlinked'); loadProgram(); }
    catch { toast.error('Failed'); }
  };

  // Items
  const handleAddItem = async () => {
    if (!itemForm.item_id && !itemForm.item_name) { toast.error('Select an item from Item Master'); return; }
    try {
      await programsAPI.addItem(id, itemForm);
      toast.success('Item added');
      setShowAddItem(false);
      setItemForm({ item_id: '', item_name: '', item_unique_id: '', item_type: 'sale', quantity: 0, unit_price: 0, description: '' });
      loadProgram();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateItem = async (itemId, updates) => {
    try {
      await programsAPI.updateItem(id, itemId, updates);
      toast.success('Item updated');
      loadProgram();
    } catch { toast.error('Failed'); }
  };

  const handleSyncPrices = async (thresholdPct = 0) => {
    try {
      const res = await programsAPI.syncPrices(id, thresholdPct);
      if (res.data.synced_count > 0) {
        toast.success(`Synced ${res.data.synced_count} item price(s) to latest actuals`);
      } else {
        toast.info(res.data.message || 'No prices needed syncing');
      }
      loadProgram();
    } catch (err) { toast.error(err.response?.data?.detail || 'Sync failed'); }
  };

  const handleRemoveItem = async (itemId) => {
    if (!window.confirm('Remove this item?')) return;
    try { await programsAPI.removeItem(id, itemId); toast.success('Removed'); loadProgram(); }
    catch { toast.error('Failed'); }
  };

  // Tasks
  const handleAddTask = async () => {
    if (!taskForm.title) { toast.error('Title required'); return; }
    try {
      await programsAPI.createTask(id, { ...taskForm, depends_on: taskForm.depends_on || [] });
      toast.success('Task created');
      setShowAddTask(false);
      setTaskForm({ title: '', description: '', assigned_to: '', due_date: '', priority: 'medium', depends_on: [] });
      loadProgram();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleUpdateTask = async (taskId, updates) => {
    try {
      await programsAPI.updateTask(id, taskId, updates);
      toast.success('Task updated');
      setEditingTask(null);
      loadProgram();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm('Delete this task?')) return;
    try { await programsAPI.deleteTask(id, taskId); toast.success('Deleted'); loadProgram(); }
    catch { toast.error('Failed'); }
  };

  const handleDelaySubmit = async () => {
    if (!delayForm.new_due_date || !delayForm.delay_reason) {
      toast.error('Both new date and reason are required');
      return;
    }
    try {
      await programsAPI.updateTask(id, showDelayModal, {
        due_date: delayForm.new_due_date,
        delay_reason: delayForm.delay_reason,
      });
      toast.success('Due date updated');
      setShowDelayModal(null);
      setDelayForm({ new_due_date: '', delay_reason: '' });
      loadProgram();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update date'); }
  };

  // Files
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', file.name);
      await programsAPI.uploadFile(id, formData);
      toast.success('File uploaded');
      loadProgram();
    } catch { toast.error('Upload failed'); }
    finally { e.target.value = ''; }
  };

  const handleFileDownload = async (file) => {
    try {
      const res = await programsAPI.downloadFile(id, file.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url; link.setAttribute('download', file.original_name);
      document.body.appendChild(link); link.click(); link.remove();
    } catch { toast.error('Download failed'); }
  };

  const handleFileDelete = async (fileId) => {
    if (!window.confirm('Delete file?')) return;
    try { await programsAPI.deleteFile(id, fileId); toast.success('Deleted'); loadProgram(); }
    catch { toast.error('Failed'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64 text-sm text-muted-foreground">Loading...</div>;
  if (!program) return null;

  const p = program;
  const saleItems = (p.items || []).filter(i => i.item_type === 'sale');
  const purchaseItems = (p.items || []).filter(i => i.item_type === 'purchase');
  const varianceItems = (p.items || []).filter(i => (i.price_variance_pct || 0) > 10 && (i.price_source || 'Manual') !== 'Manual');
  const linkedPOIds = new Set(p.purchase_order_ids || []);
  const availablePOs = customerPOs.filter(po => !linkedPOIds.has(po.id));

  const TASK_STATUS_COLORS = {
    pending: 'bg-slate-50 text-slate-600 border-slate-200',
    in_progress: 'bg-blue-50 text-blue-700 border-blue-200',
    completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    blocked: 'bg-red-50 text-red-700 border-red-200',
  };
  const PRIORITY_COLORS = { low: 'text-slate-500', medium: 'text-amber-600', high: 'text-red-600' };

  const itemOptions = masterItems
    .filter(i => !itemTypeFilter || itemTypeFilter === 'all' || i.item_type === itemTypeFilter)
    .map(i => ({
      value: i.id,
      label: `${i.unique_id} - ${i.item_name}`,
      sub: `${i.item_type || ''} | ${i.category || ''}`,
      searchStr: `${i.unique_id} ${i.item_name} ${i.external_id || ''} ${i.category || ''}`,
    }));

  const ITEM_TYPES = [...new Set(masterItems.map(i => i.item_type).filter(Boolean))];

  return (
    <div className="space-y-4" data-testid="program-detail-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/programs/list')}><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="font-heading text-xl font-bold">{p.program_name}</h1>
              <span className="text-xs font-mono text-muted-foreground">{p.program_number}</span>
              <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
            </div>
            <p className="text-xs text-muted-foreground">Customer: {p.customer_name} ({p.customer_code})</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => navigate(`/programs/${id}/financials`)} data-testid="financials-btn">
            <TrendingUp className="w-3.5 h-3.5 mr-1" /> Financials
          </Button>
          <Button size="sm" variant="outline" onClick={() => navigate(`/programs/${id}/edit`)} data-testid="edit-btn">
            <Edit2 className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
        </div>
      </div>

      {/* Financial Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="py-3 px-4"><p className="text-[10px] text-muted-foreground">Total Sale</p><p className="text-lg font-bold font-mono text-blue-600">Rs {(p.total_sale || 0).toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="py-3 px-4"><p className="text-[10px] text-muted-foreground">Total Cost</p><p className="text-lg font-bold font-mono text-amber-600">Rs {(p.total_cost || 0).toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="py-3 px-4"><p className="text-[10px] text-muted-foreground">Margin</p><p className={`text-lg font-bold font-mono ${(p.margin || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>Rs {(p.margin || 0).toLocaleString()}</p></CardContent></Card>
        <Card><CardContent className="py-3 px-4"><p className="text-[10px] text-muted-foreground">Sale/Cost Ratio</p><p className="text-lg font-bold font-mono">{p.sale_to_cost_ratio ?? '-'}</p></CardContent></Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Linked POs */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5"><Link2 className="w-4 h-4" /> Linked Purchase Orders ({p.purchase_orders?.length || 0})</CardTitle>
              <Dialog open={showLinkPO} onOpenChange={setShowLinkPO}>
                <DialogTrigger asChild>
                  <Button size="sm" variant="outline" data-testid="link-po-btn"><Plus className="w-3.5 h-3.5 mr-1" /> Link PO</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle className="text-sm">Link Purchase Order</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    {availablePOs.length === 0 ? (
                      <p className="text-xs text-muted-foreground text-center py-4">No available POs from this customer</p>
                    ) : (
                      <Select value={selectedPO} onValueChange={setSelectedPO}>
                        <SelectTrigger className="text-xs"><SelectValue placeholder="Select PO..." /></SelectTrigger>
                        <SelectContent>
                          {availablePOs.map(po => (
                            <SelectItem key={po.id} value={po.id}>{po.po_number} - {po.po_date} {po.value ? `(Rs ${po.value.toLocaleString()})` : ''}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                    <Button size="sm" onClick={handleLinkPO} disabled={!selectedPO} className="w-full">Link</Button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {(!p.purchase_orders || p.purchase_orders.length === 0) ? (
              <p className="text-xs text-muted-foreground text-center py-4">No POs linked yet</p>
            ) : (
              <div className="space-y-1.5">
                {p.purchase_orders.map(po => (
                  <div key={po.id} className="flex items-center justify-between p-2 rounded border bg-muted/30">
                    <div>
                      <p className="text-xs font-medium">{po.po_number}</p>
                      <p className="text-[10px] text-muted-foreground">{po.po_date} | {po.value ? `Rs ${po.value.toLocaleString()}` : '-'} | {po.items?.length || 0} items</p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400 hover:text-red-600" onClick={() => handleUnlinkPO(po.id)}><Unlink className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Files */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-1.5"><FileText className="w-4 h-4" /> Files ({p.files?.length || 0})</CardTitle>
              <div>
                <input type="file" id="prog-file-upload" className="hidden" onChange={handleFileUpload} />
                <Button size="sm" variant="outline" onClick={() => document.getElementById('prog-file-upload').click()} data-testid="upload-file-btn">
                  <Upload className="w-3.5 h-3.5 mr-1" /> Upload
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {(!p.files || p.files.length === 0) ? (
              <p className="text-xs text-muted-foreground text-center py-4">No files attached</p>
            ) : (
              <div className="space-y-1.5">
                {p.files.map(f => (
                  <div key={f.id} className="flex items-center justify-between p-2 rounded border bg-muted/30">
                    <div className="min-w-0 flex items-center gap-2">
                      <FileText className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-xs font-medium truncate">{f.original_name}</p>
                        <p className="text-[10px] text-muted-foreground">{f.uploaded_by_name} - {new Date(f.uploaded_at).toLocaleDateString()}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => handleFileDownload(f)}><Download className="w-3.5 h-3.5" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => handleFileDelete(f.id)}><X className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Budget Variance Alert */}
      {varianceItems.length > 0 && (
        <Card className="border-amber-300 bg-amber-50/70" data-testid="variance-banner">
          <CardContent className="py-3 px-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex items-start gap-2 flex-1">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-amber-900">
                  Budget Variance Detected — {varianceItems.length} item{varianceItems.length > 1 ? 's' : ''} ≥ 10% off latest actuals
                </p>
                <p className="text-[11px] text-amber-800 mt-0.5">
                  {varianceItems.slice(0, 3).map(i => (
                    <span key={i.id} className="inline-block mr-3">
                      <span className="font-medium">{i.item_name}</span>:
                      <span className="ml-1">Rs {Number(i.stored_unit_price || 0).toLocaleString()}</span>
                      <span className="mx-1">→</span>
                      <span className="font-semibold">Rs {Number(i.resolved_unit_price || 0).toLocaleString()}</span>
                      <span className={`ml-1 ${i.price_variance_direction === 'up' ? 'text-red-700' : 'text-emerald-700'}`}>
                        ({i.price_variance_direction === 'up' ? '+' : '-'}{i.price_variance_pct}%)
                      </span>
                    </span>
                  ))}
                  {varianceItems.length > 3 && <span className="text-muted-foreground">+{varianceItems.length - 3} more</span>}
                </p>
              </div>
            </div>
            <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => handleSyncPrices(10)} data-testid="sync-prices-btn">
              <RefreshCw className="w-3.5 h-3.5 mr-1" /> Sync budgeted prices to latest actuals
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Sale Items */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5"><TrendingUp className="w-4 h-4 text-blue-600" /> Sale Items ({saleItems.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={() => { setItemForm(f => ({ ...f, item_type: 'sale' })); setShowAddItem(true); }} data-testid="add-sale-item-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Sale Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {saleItems.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3">No sale items</p>
          ) : (
            <div className="space-y-1.5">
              {saleItems.map(item => {
                const effPrice = item.resolved_unit_price ?? item.unit_price ?? 0;
                const isDynamic = item.price_source && item.price_source !== 'Manual';
                return (
                  <div key={item.id} className="flex items-center justify-between p-2 rounded border bg-blue-50/30">
                    <div>
                      <p className="text-xs font-medium">{item.item_name} {item.item_unique_id && <span className="font-mono text-muted-foreground">({item.item_unique_id})</span>}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Qty: {item.quantity} | Price: Rs {effPrice?.toLocaleString()} {isDynamic && <span className="inline-block px-1 ml-1 bg-emerald-100 text-emerald-700 rounded text-[9px]">{item.price_source}</span>} | Total: Rs {(item.quantity * effPrice).toLocaleString()}
                      </p>
                    </div>
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => handleRemoveItem(item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Purchase Items */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5"><ShoppingCart className="w-4 h-4 text-amber-600" /> Purchase/Cost Items ({purchaseItems.length})</CardTitle>
            <Button size="sm" variant="outline" onClick={() => { setItemForm(f => ({ ...f, item_type: 'purchase' })); setShowAddItem(true); }} data-testid="add-purchase-item-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Cost Item
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {purchaseItems.length === 0 ? (
            <p className="text-xs text-muted-foreground text-center py-3">No purchase/cost items</p>
          ) : (
            <div className="space-y-1.5">
              {purchaseItems.map(item => {
                const effPrice = item.resolved_unit_price ?? item.unit_price ?? 0;
                const isDynamic = item.price_source && item.price_source !== 'Manual';
                return (
                  <div key={item.id} className="flex items-center justify-between p-2 rounded border bg-amber-50/30">
                    <div className="flex-1">
                      <p className="text-xs font-medium">{item.item_name} {item.item_unique_id && <span className="font-mono text-muted-foreground">({item.item_unique_id})</span>}</p>
                      <p className="text-[10px] text-muted-foreground">
                        Qty: {item.quantity} | Price: Rs {effPrice?.toLocaleString()} {isDynamic && <span className="inline-block px-1 ml-1 bg-emerald-100 text-emerald-700 rounded text-[9px]">{item.price_source}</span>} | Consumed: {item.consumed_quantity || 0}/{item.quantity}
                        | Cost: Rs {((item.consumed_quantity || item.quantity) * effPrice).toLocaleString()}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {!item.consumed && (
                        <Button size="sm" variant="outline" className="h-7 text-[10px]"
                          onClick={() => handleUpdateItem(item.id, { consumed: true, consumed_quantity: item.quantity })}>
                          <CheckCircle2 className="w-3 h-3 mr-0.5" /> Mark Consumed
                        </Button>
                      )}
                      {item.consumed && <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700">Consumed</Badge>}
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => handleRemoveItem(item.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Tasks */}
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium flex items-center gap-1.5"><ListTodo className="w-4 h-4" /> Tasks ({(p.tasks || []).length})</CardTitle>
            <Button size="sm" variant="outline" onClick={() => setShowAddTask(true)} data-testid="add-task-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Task
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {(!p.tasks || p.tasks.length === 0) ? (
            <p className="text-xs text-muted-foreground text-center py-4">No tasks yet</p>
          ) : (
            <div className="space-y-2">
              {p.tasks.map(task => {
                const depNames = (task.depends_on || []).map(depId => {
                  const dep = p.tasks.find(t => t.id === depId);
                  return dep ? dep.title : depId;
                });
                return (
                  <div key={task.id} className="p-2.5 rounded border bg-muted/30" data-testid={`task-${task.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className={`text-[10px] ${TASK_STATUS_COLORS[task.status] || ''}`}>{task.status}</Badge>
                        <span className={`text-[10px] font-semibold ${PRIORITY_COLORS[task.priority] || ''}`}>{task.priority?.toUpperCase()}</span>
                        <p className="text-xs font-medium">{task.title}</p>
                      </div>
                      <div className="flex items-center gap-1">
                        <Select value={task.status} onValueChange={v => handleUpdateTask(task.id, { status: v })}>
                          <SelectTrigger className="h-7 w-28 text-[10px]"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">Pending</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="blocked">Blocked</SelectItem>
                          </SelectContent>
                        </Select>
                        {task.due_date && task.status !== 'completed' && (
                          <Button size="sm" variant="ghost" className="h-7 text-[10px] text-amber-600"
                            onClick={() => { setShowDelayModal(task.id); setDelayForm({ new_due_date: '', delay_reason: '' }); }}
                            data-testid={`change-date-${task.id}`}>
                            <CalendarClock className="w-3 h-3 mr-0.5" /> Date
                          </Button>
                        )}
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-400" onClick={() => handleDeleteTask(task.id)}><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    </div>
                    <div className="mt-1 text-[10px] text-muted-foreground space-y-0.5">
                      {task.description && <p>{task.description}</p>}
                      <p>
                        {task.assigned_to_name && <span>Assigned: {task.assigned_to_name}</span>}
                        {task.due_date && <span className="ml-2">Due: {task.due_date}</span>}
                        {task.is_delayed && <Badge variant="outline" className="ml-1 text-[9px] bg-amber-50 text-amber-700 border-amber-200">Delayed</Badge>}
                        {task.original_due_date && <span className="ml-1 line-through text-muted-foreground/50">Orig: {task.original_due_date}</span>}
                        {depNames.length > 0 && <span className="ml-2">Depends on: {depNames.join(', ')}</span>}
                      </p>
                      {task.delay_history?.length > 0 && (
                        <p className="text-amber-600">Delays: {task.delay_history.length} — Last: "{task.delay_history[task.delay_history.length - 1]?.reason}"</p>
                      )}
                      {task.updated_by_name && <p className="text-muted-foreground/60">Updated by {task.updated_by_name} - {task.updated_at?.slice(0,10)}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Item Dialog */}
      <Dialog open={showAddItem} onOpenChange={v => { setShowAddItem(v); if (!v) setItemTypeFilter(''); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm">Add {itemForm.item_type === 'sale' ? 'Sale' : 'Cost'} Item from Item Master</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Filter by Item Type</Label>
              <Select value={itemTypeFilter} onValueChange={setItemTypeFilter}>
                <SelectTrigger className="h-8 text-xs mt-1" data-testid="item-type-filter"><SelectValue placeholder="All Types" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Select Item from Item Master *</Label>
              <SearchableSelect
                options={itemOptions}
                value={itemForm.item_id}
                onSelect={v => {
                  const selected = masterItems.find(i => i.id === v);
                  setItemForm(f => ({
                    ...f,
                    item_id: v,
                    item_name: selected ? selected.item_name : '',
                    item_unique_id: selected ? selected.unique_id : '',
                  }));
                }}
                placeholder="Search Item Master..."
                emptyText="No items found"
                testId="program-item-select"
              />
            </div>
            {itemForm.item_id && (
              <div className="p-2 rounded bg-muted/50 text-xs">
                <span className="font-mono">{itemForm.item_unique_id}</span> — {itemForm.item_name}
              </div>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Quantity</Label>
                <Input type="number" className="h-8 text-xs mt-1" value={itemForm.quantity} onChange={e => setItemForm(f => ({ ...f, quantity: parseFloat(e.target.value) || 0 }))} data-testid="item-quantity" />
              </div>
              <div>
                <Label className="text-xs">Unit Price (Rs) <span className="text-muted-foreground font-normal">· optional</span></Label>
                <Input type="number" className="h-8 text-xs mt-1" placeholder="Leave 0 to auto-resolve"
                  value={itemForm.unit_price} onChange={e => setItemForm(f => ({ ...f, unit_price: parseFloat(e.target.value) || 0 }))}
                  data-testid="item-price" />
                <p className="text-[10px] text-muted-foreground mt-1">
                  Auto-resolved at display: {itemForm.item_type === 'sale' ? 'from linked customer PO' : 'from latest purchase receipt / PO rate'}.
                </p>
              </div>
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input className="h-8 text-xs mt-1" value={itemForm.description} onChange={e => setItemForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <Button size="sm" className="w-full" onClick={handleAddItem} data-testid="confirm-add-item">Add Item</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={showAddTask} onOpenChange={setShowAddTask}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm">New Task</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Title *</Label>
              <Input className="h-8 text-xs mt-1" value={taskForm.title} onChange={e => setTaskForm(f => ({ ...f, title: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Description</Label>
              <Input className="h-8 text-xs mt-1" value={taskForm.description} onChange={e => setTaskForm(f => ({ ...f, description: e.target.value }))} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Assignee</Label>
                <Select value={taskForm.assigned_to} onValueChange={v => setTaskForm(f => ({ ...f, assigned_to: v }))}>
                  <SelectTrigger className="h-8 text-xs mt-1"><SelectValue placeholder="Select user..." /></SelectTrigger>
                  <SelectContent>
                    {users.map(u => (<SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Due Date</Label>
                <Input type="date" className="h-8 text-xs mt-1" value={taskForm.due_date} onChange={e => setTaskForm(f => ({ ...f, due_date: e.target.value }))} />
              </div>
            </div>
            <div>
              <Label className="text-xs">Priority</Label>
              <Select value={taskForm.priority} onValueChange={v => setTaskForm(f => ({ ...f, priority: v }))}>
                <SelectTrigger className="h-8 text-xs mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(p.tasks || []).length > 0 && (
              <div>
                <Label className="text-xs">Depends On (select tasks this depends on)</Label>
                <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                  {p.tasks.map(t => (
                    <label key={t.id} className="flex items-center gap-2 text-xs cursor-pointer">
                      <input type="checkbox" checked={taskForm.depends_on.includes(t.id)}
                        onChange={e => {
                          setTaskForm(f => ({
                            ...f,
                            depends_on: e.target.checked ? [...f.depends_on, t.id] : f.depends_on.filter(x => x !== t.id)
                          }));
                        }} />
                      {t.title}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <Button size="sm" className="w-full" onClick={handleAddTask}>Create Task</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Due Date (Delay) Dialog */}
      <Dialog open={!!showDelayModal} onOpenChange={() => setShowDelayModal(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="text-sm flex items-center gap-2"><CalendarClock className="w-4 h-4 text-amber-600" /> Change Due Date</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-muted-foreground">Extending the due date requires a reason for the delay.</p>
            <div>
              <Label className="text-xs">New Due Date *</Label>
              <Input type="date" className="h-8 text-xs mt-1" value={delayForm.new_due_date}
                onChange={e => setDelayForm(f => ({ ...f, new_due_date: e.target.value }))}
                data-testid="delay-new-date" />
            </div>
            <div>
              <Label className="text-xs">Reason for Delay *</Label>
              <Textarea className="text-xs mt-1" rows={3} value={delayForm.delay_reason}
                onChange={e => setDelayForm(f => ({ ...f, delay_reason: e.target.value }))}
                placeholder="Explain why the due date needs to be extended..."
                data-testid="delay-reason" />
            </div>
            <Button size="sm" className="w-full" onClick={handleDelaySubmit} data-testid="delay-submit">Update Due Date</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
